import React, { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { AspectRatio } from "@/components/ui/aspect-ratio";

// EditorialImage — two modes:
//
//   1. Default (fixed ratio):
//      <EditorialImage src=... ratio={4/5} />
//      Wraps the image in a Shadcn <AspectRatio> with object-cover so the
//      image fully fills the container, cropping if needed. Used by
//      cards/grids where layout consistency outweighs honoring the source
//      image's intrinsic aspect.
//
//   2. Natural mode (Phase 7 — /atelier polish):
//      <EditorialImage src=... natural />
//      Skips the <AspectRatio> wrapper entirely. Image renders at its
//      intrinsic aspect ratio via `w-full h-auto`. Used by the gallery
//      view (/atelier) and the print detail hero (/atelier/[id]) where
//      we want each fine-art print to display without cropping or
//      letterboxing. A tiny CLS on first paint is acceptable here — it
//      matches the editorial reference (archive.vonstuben.com/artprints).
export const EditorialImage = ({
    src,
    srcSet,
    sizes,
    alt = "",
    ratio = 4 / 5,
    natural = false,
    className,
    imgClassName,
    loading = "lazy",
    rounded = true,
    overlay = null,
    objectPosition,
}) => {
    const [loaded, setLoaded] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!ref.current) return;
        const img = ref.current;
        if (img.complete) setLoaded(true);
    }, [src]);

    // Shared <img> JSX so both modes stay in sync on attribute changes.
    // - Default mode uses `object-cover` to fill the AspectRatio box.
    // - Natural mode uses `block w-full h-auto` so the browser sizes the
    //   image to its intrinsic ratio inside the masonry column.
    const imgEl = (
        <img
            ref={ref}
            src={src}
            srcSet={srcSet}
            sizes={sizes || (srcSet ? "(max-width: 640px) 90vw, (max-width: 1024px) 45vw, 30vw" : undefined)}
            alt={alt}
            loading={loading}
            decoding="async"
            onLoad={() => setLoaded(true)}
            style={objectPosition ? { objectPosition } : undefined}
            className={cn(
                natural
                    ? "block w-full h-auto"
                    : "h-full w-full object-cover",
                "transition-[opacity,transform] duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)]",
                loaded ? "opacity-100 image-fade-in" : "opacity-0",
                imgClassName
            )}
        />
    );

    if (natural) {
        return (
            <div
                className={cn(
                    "relative overflow-hidden bg-[hsl(var(--muted))]",
                    rounded && "rounded-[2px]",
                    className
                )}
                data-natural="true"
            >
                {imgEl}
                {overlay}
            </div>
        );
    }

    return (
        <div className={cn("relative overflow-hidden bg-[hsl(var(--muted))]", rounded && "rounded-[2px]", className)}>
            <AspectRatio ratio={ratio}>
                {!loaded && <Skeleton className="absolute inset-0 h-full w-full" />}
                {imgEl}
                {overlay}
            </AspectRatio>
        </div>
    );
};
